// ---------------------------------------------------------------------------
// Aggregates of a daily/monthly series for display: decades of daily data
// (~25,500 points) don't draw reasonably on a bar chart, or summarize
// meaningfully as a single "total" — grouped into buckets instead, at
// whichever granularity CalculationResults.tsx's own selector is set to,
// rather than always annual.
// ---------------------------------------------------------------------------
import type { TextKey } from "../i18n/es.ts";
import { daysBetweenIso } from "./isoDate.ts";

export type Granularity = "daily" | "monthly" | "annual";

/**
 * i18n keys for each granularity — co-located here (not in
 * CalculationResults.tsx, which would make ContributionChart.tsx/
 * AggregatedContributionChart.tsx import back from the component that
 * imports THEM, a real circular import) so every place that needs to label
 * a granularity (the selector's own options, the summary tables' column
 * headers, both charts' titles) shares one mapping instead of three
 * copies drifting apart.
 */
export const GRANULARITY_LABEL_KEY: Record<Granularity, TextKey> = {
  daily: "resultsPanel.granularityDaily",
  monthly: "resultsPanel.granularityMonthly",
  annual: "resultsPanel.granularityAnnual",
};

/** Same granularities, as the lowercase noun used mid-sentence ("Total (Hm³/día)") instead of the selector's own capitalized option label ("Diario"). */
export const GRANULARITY_PERIOD_KEY: Record<Granularity, TextKey> = {
  daily: "resultsPanel.periodDay",
  monthly: "resultsPanel.periodMonth",
  annual: "resultsPanel.periodYear",
};

interface PeriodTotal {
  /** The date itself (daily), "YYYY-MM" (monthly), or the year as a string (annual) — always sorted, always what a chart's X axis plots directly, no separate label needed. */
  period: string;
  totalMm: number;
}

function periodKeyOf(date: string, granularity: "monthly" | "annual"): string {
  return granularity === "monthly" ? date.slice(0, 7) : date.slice(0, 4);
}

/**
 * Sums `series` (mm/day or already-converted volume/day, same either way —
 * this only ever adds) into buckets by day/month/year, using the series'
 * own ISO dates. NaN is treated as 0 instead of propagating and blanking
 * out its whole bucket (same reasoning a single missing day shouldn't
 * poison, say, its entire year's total).
 */
export function groupByPeriod(
  dates: string[],
  series: Float64Array,
  granularity: Granularity
): PeriodTotal[] {
  if (granularity === "daily") {
    return dates.map((date, i) => ({
      period: date,
      totalMm: Number.isFinite(series[i]) ? series[i] : 0,
    }));
  }
  const byPeriod = new Map<string, number>();
  for (let i = 0; i < dates.length; i++) {
    const key = periodKeyOf(dates[i], granularity);
    const value = Number.isFinite(series[i]) ? series[i] : 0;
    byPeriod.set(key, (byPeriod.get(key) ?? 0) + value);
  }
  return [...byPeriod.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([period, totalMm]) => ({ period, totalMm }));
}

/**
 * The finest granularity `dates` can actually support — "monthly" if
 * consecutive dates are ~28+ days apart (a genuinely monthly-native
 * project, e.g. one using the GR2M model, see gr2m.ts), "daily" otherwise.
 * Decides which options CalculationResults.tsx's granularity selector
 * offers at all — no "Daily" choice for a project whose finest real data
 * already IS monthly, there's nothing finer to show.
 */
export function nativeGranularity(dates: string[]): "daily" | "monthly" {
  if (dates.length < 2) return "daily";
  return daysBetweenIso(dates[0], dates[1]) > 2 ? "monthly" : "daily";
}
